/**
 * The embeddable "request a quote" widget: option vocabulary, defaults, and
 * the sanitizer everything reads stored or user-supplied settings through.
 *
 * Shared between the Worker (which renders the public form and serves the
 * widget's config endpoint) and the web app (which edits the settings and
 * shows a live preview), so both sides agree on what every option means and
 * what an out-of-range value falls back to.
 *
 * The sanitizer is deliberately forgiving: settings arrive from three places —
 * the database, the settings form, and query-string overrides on the embed URL
 * (used by the live preview and by anyone hand-tuning one particular embed) —
 * and a bad value in any of them should cost that one field its customization,
 * never the whole form.
 */

export const WIDGET_MODES = ['float', 'inline'] as const;
export type WidgetMode = (typeof WIDGET_MODES)[number];

export const WIDGET_POSITIONS = ['bottom-right', 'bottom-left'] as const;
export type WidgetPosition = (typeof WIDGET_POSITIONS)[number];

export const WIDGET_THEMES = ['light', 'dark', 'auto'] as const;
export type WidgetTheme = (typeof WIDGET_THEMES)[number];

export const WIDGET_CORNERS = ['rounded', 'sharp', 'pill'] as const;
export type WidgetCorners = (typeof WIDGET_CORNERS)[number];

export const WIDGET_ICONS = ['sparkle', 'chat', 'broom', 'calendar'] as const;
export type WidgetIcon = (typeof WIDGET_ICONS)[number];

/**
 * How much the form asks. 'full' is every question the inbox can use;
 * 'essential' drops the property-detail row (type, bedrooms, bathrooms,
 * square feet) for sites that want the shortest possible form.
 */
export const WIDGET_FIELD_SETS = ['full', 'essential'] as const;
export type WidgetFieldSet = (typeof WIDGET_FIELD_SETS)[number];

export const WIDGET_BUTTON_LABEL_MAX = 40;
export const WIDGET_HEADLINE_MAX = 80;
export const WIDGET_INTRO_MAX = 300;

/** Matches the app's own --color-brand, so an untouched widget looks native. */
export const WIDGET_DEFAULT_ACCENT = '#0f5c4a';

export interface WidgetSettings {
  mode: WidgetMode;
  position: WidgetPosition;
  theme: WidgetTheme;
  corners: WidgetCorners;
  icon: WidgetIcon;
  fieldSet: WidgetFieldSet;
  /** Always a #rrggbb hex color; anything else falls back to the default. */
  accentColor: string;
  /** Null on the text fields means "use the built-in wording". */
  buttonLabel: string | null;
  headline: string | null;
  intro: string | null;
  showLogo: boolean;
}

export const DEFAULT_WIDGET_SETTINGS: WidgetSettings = {
  mode: 'float',
  position: 'bottom-right',
  theme: 'light',
  corners: 'rounded',
  icon: 'sparkle',
  fieldSet: 'full',
  accentColor: WIDGET_DEFAULT_ACCENT,
  buttonLabel: null,
  headline: null,
  intro: null,
  showLogo: true,
};

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

export function isWidgetAccentColor(value: string): boolean {
  return HEX_COLOR.test(value);
}

function oneOf<T extends string>(allowed: readonly T[], raw: unknown, fallback: T): T {
  return typeof raw === 'string' && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
}

function boundedText(raw: unknown, max: number): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim().slice(0, max);
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Whatever shape arrives, a complete `WidgetSettings` comes back. Field by
 * field: recognized values pass, everything else lands on the default.
 */
export function sanitizeWidgetSettings(raw: unknown): WidgetSettings {
  const source = raw !== null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    mode: oneOf(WIDGET_MODES, source.mode, DEFAULT_WIDGET_SETTINGS.mode),
    position: oneOf(WIDGET_POSITIONS, source.position, DEFAULT_WIDGET_SETTINGS.position),
    theme: oneOf(WIDGET_THEMES, source.theme, DEFAULT_WIDGET_SETTINGS.theme),
    corners: oneOf(WIDGET_CORNERS, source.corners, DEFAULT_WIDGET_SETTINGS.corners),
    icon: oneOf(WIDGET_ICONS, source.icon, DEFAULT_WIDGET_SETTINGS.icon),
    fieldSet: oneOf(WIDGET_FIELD_SETS, source.fieldSet, DEFAULT_WIDGET_SETTINGS.fieldSet),
    accentColor:
      typeof source.accentColor === 'string' && isWidgetAccentColor(source.accentColor.trim())
        ? source.accentColor.trim().toLowerCase()
        : DEFAULT_WIDGET_SETTINGS.accentColor,
    buttonLabel: boundedText(source.buttonLabel, WIDGET_BUTTON_LABEL_MAX),
    headline: boundedText(source.headline, WIDGET_HEADLINE_MAX),
    intro: boundedText(source.intro, WIDGET_INTRO_MAX),
    showLogo: typeof source.showLogo === 'boolean' ? source.showLogo : DEFAULT_WIDGET_SETTINGS.showLogo,
  };
}

/** The stored column (JSON text or NULL) → settings. Bad JSON = defaults. */
export function parseWidgetSettings(stored: string | null | undefined): WidgetSettings {
  if (!stored) return { ...DEFAULT_WIDGET_SETTINGS };
  try {
    return sanitizeWidgetSettings(JSON.parse(stored));
  } catch {
    return { ...DEFAULT_WIDGET_SETTINGS };
  }
}

/** WCAG relative luminance of our dark ink (#17202a). */
const INK_LUMINANCE = 0.0144;

/**
 * Ink or white, whichever actually reads better against the accent: both
 * WCAG contrast ratios are computed and the larger wins. Not a luminance
 * threshold — the classic 0.179 crossover assumes the dark candidate is pure
 * black, and against #17202a it hands mid-tone accents the worse of the two
 * foregrounds. The widget button and the form's primary button both pick
 * their text color with this rather than assuming white.
 */
export function accentForeground(hex: string): '#ffffff' | '#17202a' {
  const safe = isWidgetAccentColor(hex) ? hex : WIDGET_DEFAULT_ACCENT;
  const channel = (offset: number) => {
    const value = Number.parseInt(safe.slice(offset, offset + 2), 16) / 255;
    return value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
  const againstWhite = 1.05 / (luminance + 0.05);
  const againstInk = (luminance + 0.05) / (INK_LUMINANCE + 0.05);
  return againstInk >= againstWhite ? '#17202a' : '#ffffff';
}
